import * as Sentry from "@sentry/react";
import { supabaseIntegration } from "@supabase/sentry-js-integration";
import type { Router } from "@tanstack/react-router";
import { supabaseClient } from "./supabase";

// Get Sentry DSN from environment variable
// This allows different DSNs for dev vs production
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

// Determine environment based on hostname
const getEnvironment = () => {
    if (window.location.hostname.includes("localhost")) return "local";
    const hostname = window.location.hostname;
    if (hostname.includes("dev.")) return "development";
    if (hostname.includes("staging")) return "staging";
    // Match production domain - use consistent "production" environment name
    if (hostname === "app.patient.watch") {
        return "production";
    }
    // Vercel preview deployments should not share the production bucket
    if (hostname.endsWith(".vercel.app")) return "preview";
    return "production";
};

const environment = getEnvironment();

// Only initialize Sentry if we're not in local development AND we have a DSN
if (environment !== "local" && SENTRY_DSN) {
    Sentry.init({
        dsn: SENTRY_DSN,
        environment,
        // Set release version for tracking
        release: `patient-watch@${import.meta.env.VITE_APP_VERSION || "unknown"}`,
        // Enable performance monitoring
        tracesSampleRate: environment === "production" ? 0.2 : 1.0, // Sample 20% in production, 100% in other environments
        // Configure error sampling
        sampleRate: environment === "production" ? 0.8 : 1.0, // Sample 80% in production, 100% in other environments
        // Session replay sampling
        replaysSessionSampleRate: environment === "production" ? 0.1 : 0.5, // 10% in prod, 50% in dev
        replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
        ignoreErrors: [
            // Transient network errors - match with and without the TypeError prefix
            // because the Supabase Sentry integration re-wraps them as plain Error.
            "Failed to fetch",
            "Load failed",
            "NetworkError when attempting to fetch resource",
            "TypeError: cancelled",
            "AbortError",
            // WEBAPP-6T: browser-extension noise ("Object Not Found Matching Id").
            /Object Not Found Matching Id/i,
            // WEBAPP-94 / WEBAPP-DB: a deploy leaves an open tab requesting the previous chunk.
            // The server returns index.html, and the browser rejects that as a script.
            /not a valid JavaScript MIME type/i,
            /Failed to fetch dynamically imported module/i,
            /Importing a module script failed/i,
            /evaluating ['"]e\[t\?\?/i,
            // WEBAPP-9J: Google Translate and similar extensions detach React nodes.
            /Failed to execute 'removeChild' on 'Node'/i,
            /The node to be removed is not a child of this node/i,
        ],
        integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration({
                maskAllText: true,
                blockAllMedia: true,
            }),
            supabaseIntegration(supabaseClient, Sentry, {
                tracing: true,
                breadcrumbs: true,
                errors: true,
            }),
        ],
    });
} else if (environment !== "local" && !SENTRY_DSN) {
    console.warn("Sentry DSN not configured. Error tracking is disabled.");
}

// Initialize Tanstack Router integration
export function initializeSentryRouter(router: Router) {
    if (environment !== "local" && SENTRY_DSN) {
        const routerIntegration = Sentry.tanstackRouterBrowserTracingIntegration(router);
        Sentry.addIntegration(routerIntegration);
    }
}

// Set user context for Sentry (id only - no email / patient identifiers)
export function setSentryUser(user: { id: string; role?: string; organizationId?: string }) {
    if (environment === "local") return;
    Sentry.setUser({
        id: user.id,
        role: user.role,
        organizationId: user.organizationId,
    });
}

// Clear user context on logout
export function clearSentryUser() {
    if (environment === "local") return;
    Sentry.setUser(null);
}

// Export a function to manually capture errors (useful for controlled error reporting)
export function captureError(error: Error, context?: Record<string, unknown>) {
    if (environment === "local") return;
    Sentry.captureException(error, { extra: context });
}

// Export a function to manually capture messages
export function captureMessage(message: string, level: Sentry.SeverityLevel = "info") {
    if (environment === "local") return;
    Sentry.captureMessage(message, level);
}
